import { Badge, Checkbox } from "@nabble/ui";
import { type PickableRole, sortRoles } from "../model/organizationRoles";

interface RolePickerProps {
  roles: PickableRole[];
  /** Ids of the roles held. */
  value: string[];
  onChange: (roleIds: string[]) => void;
  /** Name of the group; shown above it when `showLabel`. */
  label?: string;
  showLabel?: boolean;
}

const NOT_ASSIGNABLE = "Only a suite administrator can give or take away this role: it reaches the whole suite.";

function hint(role: PickableRole): string {
  if (role.assignable === false) return NOT_ASSIGNABLE;
  return role.description;
}

/**
 * Picks the roles a person holds in one organization (docs/adr/0022): any combination of the base
 * roles and custom roles, at least one. Base roles come first.
 */
export function RolePicker({ roles, value, onChange, label = "Roles", showLabel = false }: RolePickerProps): React.JSX.Element {
  const toggle = (id: string, on: boolean): void => onChange(on ? [...value, id] : value.filter((held) => held !== id));
  return (
    <div className="nb-field">
      {showLabel ? <span className="nb-label">{label}</span> : null}
      <div className="nb-select-list" role="group" aria-label={label}>
        {sortRoles(roles).map((role) => (
          <Checkbox
            key={role.id}
            label={
              <>
                {role.name}
                {role.system ? (
                  <>
                    {" "}
                    <Badge label="Base role" tone="neutral" dot={false} />
                  </>
                ) : null}
              </>
            }
            description={hint(role)}
            checked={value.includes(role.id)}
            disabled={role.assignable === false}
            onChange={(e) => toggle(role.id, e.target.checked)}
          />
        ))}
      </div>
      {value.length === 0 ? <p className="nb-help">Choose at least one role.</p> : null}
    </div>
  );
}
